import * as React from 'react';
import styled, { css, DefaultTheme } from 'styled-components';
import { memoizeFunction } from '@agilepoint/utils-common';
import { isDateInRange } from '@agilepoint/utils-date-time';
import { IBaseComponentProps } from '../types';
import { ListView, IItemRenderProps } from '../listview';
import { ICompactTimePickerOption } from './compact-time-picker-props';
import { parseTime } from './utils';
import { Icon } from '../icon';
import { CompactTimePickerStates } from './theme';
import { FlexBox } from '../flex-box';
import { selectThemeSemantics } from '../../styling';

interface IListviewItemContentWrapperProps {
    $isDisabled: boolean;
    $isFocused: boolean;
    $isSelected: boolean;
}

const getListViewItemStyleByState = memoizeFunction((state: CompactTimePickerStates) => {
    const getStyles = (theme: DefaultTheme) => {
        const { compactTimePicker: { colors } } = theme;
        return colors[state];
    };
    return css`
        background-color: ${({ theme }) => getStyles(theme).listViewItemBackgroundColor};
        color: ${({ theme }) => getStyles(theme).listViewItemTextColor};
    `;
});

const StyledListviewItemContentWrapper = styled(FlexBox) <IListviewItemContentWrapperProps>`
    border: none;
    box-sizing: border-box;
    height: 100%;
    pointer-events: ${({ $isDisabled }) => ($isDisabled ? 'none' : 'auto')};
    padding: ${({ theme: { semantics: { gap } } }) => `${gap.none} ${gap.l} ${gap.none} ${gap.xxxl}`};
    ${({ $isSelected, $isFocused, $isDisabled }) => getListViewItemStyleByState($isDisabled ? 'disabled' : ($isSelected ? 'selected' : ($isFocused ? 'focussed' : 'default')))}
    
    &:hover {
        ${({ $isSelected, $isFocused, $isDisabled }) => getListViewItemStyleByState($isDisabled ? 'disabled' : ($isSelected ? 'selected' : ($isFocused ? 'focussed' : 'hovered')))}
    };
`;

const StyledListView = styled(ListView)`
    border: none;

    &:focus {
        outline: none;
    }
`;

const CheckmarkIcon = styled(Icon).attrs({
    iconName: 'ap-medium-checkmark.outline'
}) <{ $isSelected: boolean }>`
    position: absolute;
    top: 50%;
    left: 5px;
    transform: translateY(-50%);
    float: left;
    display: ${({ $isSelected }) => ($isSelected ? 'inline-block' : 'none')};
    fill: ${selectThemeSemantics(({ palette }) => palette.white)};
`;

const getItemsToDisplay = memoizeFunction((itemsToDisplay: ICompactTimePickerOption[], hideDisabledItems?: boolean) => (hideDisabledItems ? itemsToDisplay.filter(i => !i.disabled) : itemsToDisplay));

export const formatTimeInRange = memoizeFunction((minTime: string, maxTime: string) => {

    const formattedMinDateTime = parseTime(minTime);
    const formattedMaxDateTime = parseTime(maxTime);

    return {
        formattedMinDateTime,
        formattedMaxDateTime
    };
});

interface IPickerProps extends IBaseComponentProps<HTMLDivElement> {
    itemHeight?: number;
    items: string[];
    selectedKey?: string[];
    minTime?: string;
    maxTime?: string;
    interval: number;
    is12Hour?: boolean;
    hideDisabledItems?: boolean;
    onChange: (args: any) => void;
}

interface IPickerState {
    itemsToDisplay: ICompactTimePickerOption[];
}

export class Picker extends React.PureComponent<IPickerProps, IPickerState> {

    constructor(props: IPickerProps) {
        super(props);
        const { items } = props;
        const itemsToDisplay = items.map(i => this.prepareOptionItem(i.trim()));
        this.state = {
            itemsToDisplay
        };
    }

    componentDidUpdate(prevProps: IPickerProps) {
        const { items, hideDisabledItems, minTime, maxTime, interval, is12Hour } = this.props;
        const { hideDisabledItems: prevHideDisabledItems, minTime: prevMinTime, maxTime: prevMaxTime, interval: prevInterval, is12Hour: prevIs12Hour } = prevProps;

        if (hideDisabledItems !== prevHideDisabledItems || minTime !== prevMinTime || maxTime !== prevMaxTime ||
            interval !== prevInterval || is12Hour !== prevIs12Hour) {
            const newItems = items.map(i => this.prepareOptionItem(i.trim()));
            this.setState({
                itemsToDisplay: newItems
            });
        }
    }

    render() {
        const { itemHeight, selectedKey, elementRef, hideDisabledItems } = this.props;
        const { itemsToDisplay } = this.state;

        return (
            <StyledListView
                selectable="single"
                keyProp="text"
                itemHeight={itemHeight}
                selectedKeys={selectedKey}
                onChange={this.onChange}
                items={getItemsToDisplay(itemsToDisplay, hideDisabledItems)}
                isVirtualized
                allowKeyboardNavigation
                onRenderItem={this.getMarkupForItem}
                elementRef={elementRef}
                canSelectItem={this.canSelectItem}
            />
        );
    }

    private prepareOptionItem = (time: string): ICompactTimePickerOption => ({
        text: time,
        disabled: this.isItemsDisabled(time)
    })

    private canSelectItem = (item: ICompactTimePickerOption): boolean => {
        const { selectedKey = [] } = this.props;
        const { itemsToDisplay } = this.state;
        const [firstKey] = selectedKey;
        return itemsToDisplay.some(i => i.text !== firstKey && i.text.includes(item.text) && !i.disabled);
    }

    private onChange = (selectedKeys: string[]) => {
        const { onChange } = this.props;
        if (onChange === undefined) {
            return;
        }

        onChange(selectedKeys);
    }

    private isItemsDisabled(data: string) {
        const { minTime = '', maxTime = '' } = this.props;
        if (!maxTime || !minTime) {
            return false;
        }

        const { formattedMinDateTime, formattedMaxDateTime } = formatTimeInRange(minTime, maxTime);
        const formattedDate = parseTime(data);

        return !isDateInRange(formattedDate, formattedMinDateTime, formattedMaxDateTime);
    }

    private getMarkupForItem = ({ isFocused, data, toggleSelection }: IItemRenderProps) => {
        const { selectedKey = [] } = this.props;
        const item = data as ICompactTimePickerOption;
        return (
            <StyledListviewItemContentWrapper
                alignItems="center"
                $isSelected={selectedKey.includes(item.text)}
                $isFocused={isFocused}
                $isDisabled={item.disabled}
                onClick={toggleSelection}>
                <CheckmarkIcon $isSelected={selectedKey.includes(item.text)} />
                <div>{item.text}</div>
            </StyledListviewItemContentWrapper>
        );
    }
}
